// ABOUTME: Export utilities for downloading Pure queries and CSV result data.
// ABOUTME: Provides browser-side file generation and download trigger functions.

/**
 * Structured result data from an execute_pure_query tool call.
 * Matches the JSON shape returned by the backend.
 */
export interface PureQueryResult {
  pure_query: string;
  generated_sql: string | null;
  columns: string[];
  rows: (string | number | boolean | null)[][];
  row_count: number;
  execution_time_ms: number;
}

/**
 * Try to parse a tool result content block as a PureQueryResult.
 * Returns null if the content doesn't match the expected shape.
 */
export function parsePureQueryResult(content: unknown): PureQueryResult | null {
  if (!content || typeof content !== 'object') return null;

  const obj = content as Record<string, unknown>;

  // Must have pure_query and columns to be a valid result
  if (typeof obj.pure_query !== 'string' || !Array.isArray(obj.columns)) {
    return null;
  }

  return {
    pure_query: obj.pure_query as string,
    generated_sql: (obj.generated_sql as string) ?? null,
    columns: obj.columns as string[],
    rows: (obj.rows as (string | number | boolean | null)[][]) ?? [],
    row_count: (obj.row_count as number) ?? 0,
    execution_time_ms: (obj.execution_time_ms as number) ?? 0,
  };
}

/**
 * Convert tabular data (columns + positional rows) to a CSV string.
 * Handles quoting for values containing commas, quotes, or newlines.
 */
export function toCsv(columns: string[], rows: unknown[][]): string {
  const escapeCell = (val: unknown): string => {
    if (val === null || val === undefined) return '';
    const str = String(val);
    // Quote if the value contains comma, double-quote, or newline
    if (str.includes(',') || str.includes('"') || str.includes('\n')) {
      return '"' + str.replace(/"/g, '""') + '"';
    }
    return str;
  };

  const lines: string[] = [];
  lines.push(columns.map(escapeCell).join(','));
  for (const row of rows) {
    lines.push(row.map(escapeCell).join(','));
  }
  return lines.join('\n');
}

/**
 * Trigger a browser file download with the given content.
 */
export function downloadFile(content: string, filename: string, mimeType: string = 'text/plain'): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Export a single Pure query result as CSV.
 */
export function exportResultAsCsv(result: PureQueryResult, filename?: string): void {
  const csv = toCsv(result.columns, result.rows);
  const name = filename ?? `query-result-${Date.now()}.csv`;
  downloadFile(csv, name, 'text/csv');
}

/**
 * Export one or more Pure queries as a .pure file.
 * Each query is separated by a blank line.
 */
export function exportQueries(queries: string[], filename?: string): void {
  const content = queries.join('\n\n');
  const name = filename ?? `pure-queries-${Date.now()}.pure`;
  downloadFile(content, name, 'text/plain');
}
